import { AI_ACTION_SOURCES, type AiActionSource, type AiHistoryEntry } from '@fos/shared/contracts/ai-history';
import { AI_ACTION_SOURCE_LABELS, AI_ACTION_STATUS_LABELS, AI_ACTION_STATUS_TONES } from '@fos/shared/labels';
import { Bot, MessagesSquare } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useSession } from '../../lib/auth';
import { formatDateTimeShort, formatRelativePast, plural } from '../../lib/format';
import { trpc } from '../../lib/trpc';
import { useAccess } from '../../lib/useAccess';
import {
  Avatar,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  ListSkeleton,
  Section,
  Segmented,
  type SegmentedOption,
  Status,
} from '../../ui';
import { StatusPill } from '../../ui/StatusPill';
import { askAi } from '../copilot/ask-ai-store';
import { useResumeConversation } from '../copilot/components/ConversationHistory';
import { formatAiCost } from '../copilot/copilot-ui';
import { recordHref } from './history-format';

/**
 * Historia AI (Fala 10, Agenci → „Historia AI”): działania AI w workspace z poleceniem, które je wywołało — źródło
 * (Copilot / Claude przez MCP / automatyzacja / Zleć Claude), kto, kiedy, co zrobiono z linkiem do rekordu, koszt
 * (z „Widzi stawki i kwoty”); dla MCP — wywołane narzędzie i pole `note` od agenta. Serwer pokazuje tylko działania
 * na rekordach, które osoba widzi (`history.aiActions`); własną rozmowę z Copilotem można wznowić.
 */

const PAGE = 30;
type SourceFilter = 'all' | AiActionSource;

const SOURCE_OPTIONS: SegmentedOption<SourceFilter>[] = [
  { value: 'all', label: 'Wszystko' },
  ...AI_ACTION_SOURCES.map((s) => ({ value: s, label: AI_ACTION_SOURCE_LABELS[s] })),
];

function Command({ text, label }: { text: string; label: string }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 280 || text.split('\n').length > 4;
  return (
    <div className="mt-2 rounded-lg bg-surface px-3 py-2">
      <p className="text-caption text-fg-muted">{label}</p>
      <p className={`text-body-s break-words whitespace-pre-wrap text-fg ${open || !long ? '' : 'line-clamp-4'}`}>
        {text}
      </p>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 rounded-xs text-caption text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {open ? 'Zwiń' : 'Pokaż całość'}
        </button>
      )}
    </div>
  );
}

function EntryCard({ entry, now }: { entry: AiHistoryEntry; now: Date }) {
  const resume = useResumeConversation();
  const person = entry.person;
  return (
    <li className="flex gap-3 border-b border-line py-4 last:border-b-0">
      {person ? (
        <Avatar initials={person.initials} color={person.avatarColor} title={person.name} />
      ) : (
        <Avatar ai initials="A" title={entry.sourceLabel} />
      )}
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-s">
          <span className="font-medium text-fg">{person?.name ?? 'Automatyzacja'}</span>
          <span className="text-fg-secondary">· {entry.sourceLabel}</span>
          <time
            className="text-caption text-fg-muted"
            dateTime={new Date(entry.at).toISOString()}
            title={formatDateTimeShort(entry.at, now)}
          >
            · {formatRelativePast(entry.at, now)}
          </time>
          {entry.status && (
            <StatusPill tone={entry.status.tone} size="s">
              {entry.status.label}
            </StatusPill>
          )}
          {entry.simulated && <Status tone="warning">Symulacja</Status>}
        </p>
        {entry.client && (
          <p className="mt-0.5 text-caption text-fg-muted">
            Klient:{' '}
            <Link to={`/klienci/${entry.client.id}`} className="text-accent hover:underline">
              {entry.client.name}
            </Link>
          </p>
        )}
        {entry.command && (
          <Command
            text={entry.command}
            label={
              entry.source === 'mcp'
                ? 'Polecenie przekazane przez agenta (note)'
                : entry.source === 'automation'
                  ? 'Wyzwalacz'
                  : 'Polecenie'
            }
          />
        )}
        {!entry.command && entry.commandHidden && (
          <p className="mt-1 text-caption text-fg-muted">
            {entry.source === 'automation' ? 'Wyzwalacz' : 'Polecenie'} ukryte — prywatna poczta autora
          </p>
        )}
        {entry.tools.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1 text-caption text-fg-secondary" aria-label="Wywołane narzędzia">
            {entry.tools.map((t, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: narzędzia jednego wywołania w stałej kolejności
              <li key={i} className="flex flex-wrap items-center gap-x-2">
                <span className="font-mono text-fg">{t.name}</span>
                <Status tone={t.ok ? 'success' : 'danger'}>{t.ok ? 'OK' : (t.code ?? 'Błąd')}</Status>
              </li>
            ))}
          </ul>
        )}
        {entry.actions.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1.5" aria-label="Co zrobiono">
            {entry.actions.map((a, i) => {
              const href = recordHref(a.link);
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: działania jednego wpisu w stałej kolejności
                <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-body-s">
                  <Status tone={AI_ACTION_STATUS_TONES[a.status]}>{AI_ACTION_STATUS_LABELS[a.status]}</Status>
                  <span className="min-w-0 break-words text-fg">{a.label}</span>
                  {href && (
                    <Link to={href} className="text-caption text-accent hover:underline">
                      Otwórz
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {entry.hiddenActions > 0 && (
          <p className="mt-1 text-caption text-fg-muted">
            {entry.hiddenActions} {plural(entry.hiddenActions, ['działanie', 'działania', 'działań'])} poza Twoim
            zakresem
          </p>
        )}
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-caption text-fg-muted">
          {entry.costPln !== undefined && (
            <span>
              Koszt: {formatAiCost(entry.costPln)}
              {entry.costInformational ? ' (informacyjnie, subskrypcja)' : ''}
            </span>
          )}
          {entry.conversationId && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-accent"
              onClick={() => {
                void resume(entry.conversationId as string).then((ok) => {
                  if (ok) askAi.open();
                });
              }}
            >
              <MessagesSquare size={12} strokeWidth={1.75} aria-hidden />
              Wznów rozmowę
            </button>
          )}
        </p>
      </div>
    </li>
  );
}

function useLoadMore(onVisible: () => void, enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) cb.current();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);
  return ref;
}

export function AiHistoryView() {
  const access = useAccess();
  const session = useSession().data;
  const me = session?.authenticated ? session.user.id : null;
  const [source, setSource] = useState<SourceFilter>('all');
  const [mine, setMine] = useState(false);
  const [reads, setReads] = useState(false);
  const query = trpc.history.aiActions.useInfiniteQuery(
    {
      limit: PAGE,
      ...(source !== 'all' ? { source } : {}),
      ...(mine && me ? { userId: me } : {}),
      ...(reads ? { includeReads: true } : {}),
    },
    { getNextPageParam: (last) => last.nextCursor ?? undefined },
  );
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  const more = Boolean(query.hasNextPage);
  const sentinel = useLoadMore(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
  }, more);
  const now = new Date();

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:none] md:mx-0 md:px-0">
          <Segmented
            value={source}
            onValueChange={setSource}
            options={SOURCE_OPTIONS}
            aria-label="Źródło"
            size="s"
            className="max-md:[&>button]:h-10"
          />
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          <Checkbox checked={mine} onCheckedChange={(v) => setMine(Boolean(v))} label="Tylko moje" touchTarget />
          {(source === 'all' || source === 'mcp') && (
            <Checkbox
              checked={reads}
              onCheckedChange={(v) => setReads(Boolean(v))}
              label="Także odczyty MCP"
              touchTarget
            />
          )}
        </div>
        <p className="text-caption text-fg-muted">
          Działania AI z poleceniem, które je wywołało. Widzisz swoje działania i działania na rekordach, do których
          masz dostęp{access.flag('seesRates') ? '' : ' (bez kosztów)'}. Rozmowy z Copilotem są prywatne — tu jest tylko
          polecenie, które coś zmieniło.
        </p>
      </div>
      <Section padding="none" aria-label="Historia AI">
        <div className="px-4 md:px-8">
          {query.isPending ? (
            <div className="py-4">
              <ListSkeleton rows={4} />
            </div>
          ) : query.isError ? (
            <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
          ) : items.length === 0 ? (
            <EmptyState
              icon={Bot}
              title="Brak działań AI."
              description="Tu pojawią się działania Copilota, Claude przez MCP, automatyzacji i zlecenia dla Claude."
            />
          ) : (
            <ol aria-label="Działania AI" className="flex flex-col">
              {items.map((e) => (
                <EntryCard key={e.key} entry={e} now={now} />
              ))}
            </ol>
          )}
          <div ref={sentinel} />
          {more && (
            <div className="flex justify-center pb-4">
              <Button
                variant="ghost"
                size="s"
                onClick={() => void query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                Pokaż starsze
              </Button>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
}
